import { useQueries } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { publicationOptions } from '@/src/features/publication/services/options/publicationOptions'
import type { TPublicationCardProps } from '@/src/features/publication/ui/components/PublicationCard/PublicationCard'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'

import type { TGetSubscriptionPublication } from '../../../api/getSubscriptions.types'
import { useEverlastingSubscriptionsQuery } from '../../../services/useEverlastingSubscriptionsQuery'
import type { TComputedSubscription } from '../../../utils/getComputedSubscription'
import { EverlastingSliderBase, type TEverlastingSliderBaseProps } from './EverlastingSliderBase'

const getPublication = (subscription: TComputedSubscription): TGetSubscriptionPublication | undefined =>
  subscription.nextPublication || subscription.community.publications[0]

export const EverlastingSlider = (props: Omit<TEverlastingSliderBaseProps, 'everlastingItems'>) => {
  const { data: subscriptions, error } = useEverlastingSubscriptionsQuery()

  const everlastingItems = useQueries({
    queries: subscriptions
      ? subscriptions.map((subscription) => publicationOptions(getPublication(subscription)?.id))
      : [],
    combine: (results) =>
      subscriptions
        ? results
            .reduce<(TPublicationCardProps['publication'] | TGetSubscriptionPublication)[]>((acc, result, index) => {
              const publication = getPublication(subscriptions[index])
              if (result.data) {
                acc.push(result.data)
              } else if (publication) {
                // If publication data is not available, use the subscription's publication object, even though incomplete, as fallback
                acc.push(publication)
              }
              return acc
            }, [])
            .sort((a, b) => dayjs(b.effectivePublicationDate).valueOf() - dayjs(a.effectivePublicationDate).valueOf())
        : undefined,
  })

  if (error) return <ErrorMessage error={error} defaultErrorKey="prayer-space.everlasting.load.error" />

  return <EverlastingSliderBase everlastingItems={everlastingItems} {...props} />
}
